import {
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { componentPageStyles } from "styles/component-page-styles";
import { useTranslation } from "react-i18next";
import { CommonBgColor } from "shared/constants";
import { ReactComponent as CheatSheetIcon } from "assets/icons/cheat-sheet-icon.svg";
import { ReactComponent as CheatSheetIcon2 } from "assets/icons/cheat-sheet-icon2.svg";

const WarningIcon = () => (
  <Box
    sx={{
      width: 48,
      height: 48,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CheatSheetIcon width={48} height={48} />
  </Box>
);

const TipIcon = () => (
  <Box
    sx={{
      width: 48,
      height: 48,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CheatSheetIcon2 width={48} height={48} />
  </Box>
);

function CheatSheet() {
  const { t } = useTranslation();

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      id="ssml-guide"
      bgcolor={CommonBgColor}
    >
      <Container sx={componentPageStyles.ssml}>
        <Typography variant="h3" sx={{ mt: 4, mb: 8 }}>
          {t("ssml.cheatSheet")}
        </Typography>
        <Stack direction="column" spacing={8}>
          <Stack spacing={2}>
            <Typography variant="h4" sx={{ pb: "8px" }}>
              {t("ssml.nesting.heading")}
            </Typography>
            <Typography>{t("ssml.nesting.introduction")}</Typography>
            <Typography>{t("ssml.nesting.limitations")}</Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="h4" sx={{ mt: "64px", mb: "24px" }}>
              {t("ssml.nesting.tagsHeading")}
            </Typography>

            <TableContainer component={Paper}>
              <Table
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "14px",
                    padding: "24px",
                    width: "308px",
                    height: "80px",
                  },
                }}
                aria-label="SSML tags table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t("ssml.nesting.parentTag")}</TableCell>
                    <TableCell>{t("ssml.nesting.allowedChildTags")}</TableCell>
                    <TableCell>
                      {t("ssml.nesting.forbiddenChildTags")}
                    </TableCell>
                    <TableCell>{t("ssml.nesting.notes")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>&lt;break&gt;</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>{t("ssml.nesting.breakNote")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;say-as&gt;</TableCell>
                    <TableCell>&lt;break&gt;</TableCell>
                    <TableCell>
                      {t("ssml.nesting.allExcept")} &lt;break&gt;
                    </TableCell>
                    <TableCell>{t("ssml.nesting.sayAsNote")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;phoneme&gt;</TableCell>
                    <TableCell>&lt;break&gt;</TableCell>
                    <TableCell>
                      {t("ssml.nesting.allExcept")} &lt;break&gt;
                    </TableCell>
                    <TableCell>{t("ssml.nesting.phonemeNote")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;prosody&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.allTags")}</TableCell>
                    <TableCell>&lt;prosody&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.prosodyNote")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;emphasis&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.allTags")}</TableCell>
                    <TableCell>&lt;emphasis&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.emphasisNote")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;voice&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.allTags")}</TableCell>
                    <TableCell>&lt;voice&gt;</TableCell>
                    <TableCell>
                      {t("ssml.nesting.voiceNote")}
                      <div style={{ marginTop: "12px" }}></div>
                      {t("ssml.nesting.voiceNoteLine2")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>&lt;style&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.allTags")}</TableCell>
                    <TableCell>&lt;style&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.styleNote")}</TableCell>
                  </TableRow>
                  <TableRow sx={{ "& td": { borderBottom: "none" } }}>
                    <TableCell>&lt;lang&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.allTags")}</TableCell>
                    <TableCell>&lt;lang&gt;</TableCell>
                    <TableCell>{t("ssml.nesting.langNote")}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Stack spacing={3}>
            <Typography variant="h6" sx={{ mt: 3 }}>
              1. {t("ssml.nesting.rules.sameCategory.heading")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <WarningIcon />
              <Typography>
                {t("ssml.nesting.rules.sameCategory.warning")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TipIcon />
              <Typography>
                {t("ssml.nesting.rules.sameCategory.tip")}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={3}>
            <Typography variant="h6" sx={{ mt: 3 }}>
              2. {t("ssml.nesting.rules.prosodyBreak.heading")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <WarningIcon />
              <Typography>
                {t("ssml.nesting.rules.prosodyBreak.warning")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TipIcon />
              <Typography>
                {t("ssml.nesting.rules.prosodyBreak.tip")}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={3}>
            <Typography variant="h6" sx={{ mt: 3 }}>
              3. {t("ssml.nesting.rules.sayAsPhonemeMix.heading")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <WarningIcon />
              <Typography>
                {t("ssml.nesting.rules.sayAsPhonemeMix.warning")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TipIcon />
              <Typography>
                {t("ssml.nesting.rules.sayAsPhonemeMix.tip")}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={3}>
            <Typography variant="h6" sx={{ mt: 3 }}>
              4. {t("ssml.nesting.rules.prosodyNesting.heading")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <WarningIcon />
              <Typography>
                {t("ssml.nesting.rules.prosodyNesting.warning")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TipIcon />
              <Typography>
                {t("ssml.nesting.rules.prosodyNesting.tip")}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Typography variant="h4" sx={{ mt: 8, mb: 3 }}>
          {t("ssml.nesting.multiAttribute.heading")}
        </Typography>
        <Typography sx={{ mb: 5 }}>
          {t("ssml.nesting.multiAttribute.intro")}
        </Typography>

        <TableContainer component={Paper} sx={{ mt: 2, mb: 4 }}>
          <Table
            sx={{
              "& .MuiTableCell-root": {
                fontSize: "14px",
                padding: "24px",
                width: "308px",
                height: "80px",
              },
            }}
            aria-label="SSML attributes table"
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.attribute")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.allowedCombinations")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.forbiddenCombinations")}
                </TableCell>
                <TableCell>{t("ssml.nesting.multiAttribute.notes")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>pitch</TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.pitchAllowed")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.pitchForbidden")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.pitchNotes")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>contour</TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.contourAllowed")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.contourForbidden")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.contourNotes")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>rate</TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.rateAllowed")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.rateForbidden")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.rateNotes")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>duration</TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.durationAllowed")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.durationForbidden")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.durationNotes")}
                </TableCell>
              </TableRow>
              <TableRow sx={{ "& td": { borderBottom: "none" } }}>
                <TableCell>volume</TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.volumeAllowed")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.volumeForbidden")}
                </TableCell>
                <TableCell>
                  {t("ssml.nesting.multiAttribute.volumeNotes")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Stack>
  );
}

export default CheatSheet;
